// Types for Pulse Report Dashboard Table

export type TicketType = 'INCIDENT' | 'PROBLEM' | 'CHANGE' | 'SERVICE_REQUEST' | 'CASE';

export interface PulseReportTicket {
  // Identity fields
  id: string;
  ticketType: TicketType;

  // Core fields (1-11)
  taskType: string;
  number: string;
  slaDefinition: string | null;
  type: string;
  priority: string;
  state: string;
  shortDescription: string;
  assignmentGroup: string | null;
  assignedTo: string | null;
  company: string | null;
  customerContact: string | null;

  // SLA fields - Grouped by SLA Type (Response, Ongoing, Resolution)
  // Response SLA
  responseSlaStart: string | null;
  responseSlaTarget: string | null;
  responseSlaStatus: string | null;
  responseSlaBreachedAt: string | null;
  responseSlaBreachReason: string | null;
  // Ongoing SLA
  ongoingSlaStart: string | null;
  ongoingSlaTarget: string | null;
  ongoingSlaStatus: string | null;
  ongoingSlaBreachedAt: string | null;
  ongoingSlaBreachReason: string | null;
  // Resolution SLA
  resolutionSlaStart: string | null;
  resolutionSlaTarget: string | null;
  resolutionSlaStatus: string | null;
  resolutionSlaBreachedAt: string | null;
  resolutionSlaBreachReason: string | null;
  // Overall breach info
  slaBreached: boolean;
  breachReason: string | null;

  // Audit fields (19-24)
  createdBy: string | null;
  ticketLastUpdatedTime: string;
  lastUpdatedBy: string | null;
  channel: string | null;
  resource: string | null;
  ticketCreated: string;

  // Resolution fields (25-31)
  ticketResolved: string | null;
  ticketResolvedBy: string | null;
  ticketClosed: string | null;
  ticketClosedBy: string | null;
  onHoldReason: string | null;
  ticketClosureRemarks: string | null;
  ticketDurationMins: number | null;

  // Change-specific fields (32-36)
  changeStatus: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;

  // Closure and Problem fields (37-39)
  closureCode: string | null;
  rcaPresentedBy: string | null;
  problemDate: string | null;
}

export interface PulseReportResponse {
  tickets: PulseReportTicket[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PulseReportBaseFilters {
  customerId?: string;
  timeRange?: string;
  assignmentGroupId?: string;
  timezoneOffset?: number;
}

export interface PulseReportChartFilters {
  ticketType?: TicketType | string; // Supports comma-separated values like 'INCIDENT,SERVICE_REQUEST'
  state?: string;
  priority?: string;
  slaBreached?: string;
  filterType?: string;
  filterValue?: string;
  date?: string;
  agingBucket?: string;
  problemState?: string;
  changeState?: string;
  changeType?: string;
}

export interface ColumnConfig {
  key: keyof PulseReportTicket | string;
  header: string;
  width: string;
  minWidth?: string;
  sticky?: boolean;
  stickyOffset?: string;
  showFor?: TicketType[];
  hideFor?: TicketType[];
  formatter?: (value: any, row: PulseReportTicket) => React.ReactNode;
  // Grouped header support
  groupId?: string; // e.g., 'response', 'ongoing', 'resolution'
  isGroupChild?: boolean; // Indicates this is a child column of a group
}

// Group header configuration for ServiceNow-style grouped columns
export interface ColumnGroupConfig {
  id: string;
  header: string;
  colSpan: number;
  className?: string;
}

// Label mappings
export const priorityLabels: Record<string, string> = {
  critical_1: 'Critical',
  high_2: 'High',
  moderate_3: 'Moderate',
  low_4: 'Low',
  planning_5: 'Planning',
};

export const priorityColors: Record<string, string> = {
  critical_1: 'bg-red-100 text-red-800',
  high_2: 'bg-orange-100 text-orange-800',
  moderate_3: 'bg-yellow-100 text-yellow-800',
  low_4: 'bg-blue-100 text-blue-800',
  planning_5: 'bg-green-100 text-green-800',
};

export const stateLabels: Record<string, string> = {
  new: 'New',
  open: 'Open',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  awaiting_info: 'Awaiting Info',
  solution_proposed: 'Solution Proposed',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  resolved: 'Resolved',
  auto_resolved: 'Auto Resolved',
  closed: 'Closed',
  closed_complete: 'Closed Complete',
  closed_incomplete: 'Closed Incomplete',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
};

export const changeStateLabels: Record<string, string> = {
  new: 'New',
  assess: 'Assess',
  authorize: 'Authorize',
  scheduled: 'Scheduled',
  implement: 'Implement',
  review: 'Review',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const problemStateLabels: Record<string, string> = {
  new: 'New',
  assess_1: 'Assess 1',
  assess_2: 'Assess 2',
  root_cause_analysis: 'Root Cause Analysis',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const ticketTypeLabels: Record<string, string> = {
  CASE: 'Case',
  INCIDENT: 'Incident',
  SERVICE_REQUEST: 'Service Request',
  CHANGE: 'Change',
  PROBLEM: 'Problem',
};

export const ticketTypeColors: Record<string, string> = {
  CASE: 'bg-purple-100 text-purple-800',
  INCIDENT: 'bg-red-100 text-red-800',
  SERVICE_REQUEST: 'bg-blue-100 text-blue-800',
  CHANGE: 'bg-green-100 text-green-800',
  PROBLEM: 'bg-amber-100 text-amber-800',
};

export const channelLabels: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  portal: 'Portal',
  chat: 'Chat',
  api: 'API',
  monitoring: 'Monitoring',
};

export const slaStageLabels: Record<string, string> = {
  NORMAL: 'Normal',
  WARNING_50: '50% Warning',
  WARNING_75: '75% Warning',
  WARNING_90: '90% Warning',
  CRITICAL: 'Critical',
  BREACHED: 'Breached',
};

export const slaStageColors: Record<string, string> = {
  NORMAL: 'bg-green-100 text-green-800',
  WARNING_50: 'bg-yellow-100 text-yellow-800',
  WARNING_75: 'bg-orange-100 text-orange-800',
  WARNING_90: 'bg-red-100 text-red-700',
  CRITICAL: 'bg-red-200 text-red-800',
  BREACHED: 'bg-red-300 text-red-900',
};

export const slaStatusLabels: Record<string, string> = {
  // Uppercase keys (from DB currentState)
  RUNNING: 'Running',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  BREACHED: 'Breached',
  MET: 'Met',
  IN_PROGRESS: 'In Progress',
  'N/A': '-',
  // Mixed case keys (from API response - legacy)
  Running: 'Running',
  Paused: 'Paused',
  Met: 'Met',
  Cancelled: 'Cancelled',
  Breached: 'Breached',
};

// Color classes for SLA status badges
export const slaStatusColors: Record<string, string> = {
  // Uppercase keys
  BREACHED: 'bg-red-100 text-red-800 font-semibold',
  MET: 'bg-green-100 text-green-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  PAUSED: 'bg-yellow-100 text-yellow-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
  RUNNING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  'N/A': 'bg-gray-50 text-gray-400',
  // Mixed case (legacy)
  Breached: 'bg-red-100 text-red-800 font-semibold',
  Met: 'bg-green-100 text-green-800',
  Running: 'bg-blue-100 text-blue-800',
  Paused: 'bg-yellow-100 text-yellow-800',
  Cancelled: 'bg-gray-100 text-gray-600',
};

export const closureCodeLabels: Record<string, string> = {
  successful: 'Successful',
  successful_with_issues: 'Successful with Issues',
  failed: 'Failed',
  cancelled: 'Cancelled',
  resolved: 'Resolved',
  not_resolved: 'Not Resolved',
  duplicate: 'Duplicate',
  no_action_required: 'No Action Required',
};
